import { RadioGroup, RadioGroupItem } from "@/src/components/ui/radio-group";
import { Label } from "@/src/components/ui/label";
import { formatMoney } from "@tagadapay/plugin-sdk";
import { cn } from "@/src/lib/utils";
import type { Bundle } from "@/src/types/checkout";
import type { PluginConfig } from "@/src/types/plugin";
import { useMemo } from "react";

interface BundleSelectionProps {
  bundles: Bundle[];
  selectedBundle: string | null;
  isUpdating: boolean;
  onBundleChange: (bundleId: string) => void;
  pluginConfig: PluginConfig;
  percentageSavings: number;
  cartTimer: string;
}

export function BundleSelection({
  bundles,
  selectedBundle,
  isUpdating,
  onBundleChange,
  pluginConfig,
  percentageSavings,
  cartTimer,
}: BundleSelectionProps) {
  const memoizedSelectedBundle = useMemo(
    () => selectedBundle,
    [selectedBundle],
  );
  const memoizedBundles = useMemo(() => bundles, [bundles]);

  return (
    <div
      className={cn(
        "space-y-5 rounded-xl  transition-opacity duration-300 sm:space-y-6",
        isUpdating && "pointer-events-none opacity-50",
      )}
    >
      <div className="flex flex-col gap-2.5 ">
        <div className="flex flex-row items-center justify-center gap-2 sm:gap-2.5 rounded-[10px] border border-[rgb(197,199,210)] bg-[rgb(248,248,248)] p-2 sm:p-2.5">
          <div className="flex items-center shrink-0">
            <img
              crossOrigin="anonymous"
              src="//assets.funnelkonnekt.com/92dcb740-0f76-11ee-81cb-39883164e76c/1688297141082_1669829471_1657636070_warning.png"
              alt=""
              className="w-6 sm:w-7 md:w-[30px] h-auto lazyloaded"
            />
          </div>
          <div className="text-center text-xs sm:text-sm md:text-[.975rem] leading-tight sm:leading-[22px] font-black text-[#c8202f]">
            <b>LIMITED STOCK!</b>{" "}
            <span className="text-black font-light">
              Cart reserved for:
            </span>{" "}
            <span className="countdown">{cartTimer}</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2 sm:gap-3">
            <h3 className="text-sm sm:text-base md:text-[17px] font-bold leading-tight sm:leading-[26px] text-[rgb(48,48,48)]">
              STEP 1: PICK YOUR BUNDLE DISCOUNT
            </h3>
            {isUpdating && (
              <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-blue-600">
                <div className="h-3 w-3 sm:h-4 sm:w-4 animate-spin rounded-full border-b-2 border-blue-600"></div>
                <span className="hidden sm:inline">Updating...</span>
              </div>
            )}
          </div>
          <p className="text-sm sm:text-[15px] md:text-[16px] leading-tight sm:leading-[18px] text-[rgb(48,48,48)]">
            Buy a Bundle To Save Even More!
          </p>
        </div>
      </div>

      <div className="flex justify-between border-b border-[rgb(238,241,255)] px-1.5 sm:px-2.5 pb-[5px] text-sm sm:text-base font-bold">
        <span>Product</span>
        <span>Price</span>
      </div>

      {selectedBundle === null ? (
        <div className="flex items-center justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <p className="ml-2 text-gray-600">Loading packages...</p>
        </div>
      ) : (
        <RadioGroup
          value={memoizedSelectedBundle || undefined}
          onValueChange={onBundleChange}
          disabled={isUpdating}
          className={cn(isUpdating && "pointer-events-none opacity-75")}
        >
          {memoizedBundles.map((bundle: Bundle) => (
            <Label
              key={bundle.id}
              htmlFor={bundle.id}
              className={cn(
                "relative flex cursor-pointer touch-manipulation items-start justify-between gap-2 sm:gap-3 rounded-[3px] border border-[rgb(232,232,234)] p-2 sm:p-3 transition-all duration-200",
                {
                  "border-[3px] border-[rgb(184,135,32)] bg-[rgb(255,255,250)]":
                    bundle.id === selectedBundle,
                },
              )}
            >
              <div className="flex items-start gap-2 sm:gap-3 flex-1">
                <RadioGroupItem
                  value={bundle.id}
                  id={bundle.id}
                  className="mt-1 h-4 w-4 sm:h-5 sm:w-5 border-2"
                />
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="relative shrink-0">
                    <img
                      src={bundle.images[0] || "/placeholder.svg"}
                      alt={bundle.name}
                      className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-[2px] border border-[rgb(225,225,225)]"
                    />
                  </div>
                  <div className="flex flex-col gap-1 h-full">
                    {bundle.bestValue && (
                      <div className="w-fit bg-[rgb(255,0,0)] px-1.5 sm:px-2 py-0.5 sm:py-1 text-[9px] sm:text-[10px] font-semibold uppercase text-white">
                        MOST POPULAR
                      </div>
                    )}
                    <p className="text-sm sm:text-base md:text-lg font-semibold text-[rgb(37,42,50)]">
                      {bundle.name}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-0.5 sm:gap-1 text-right shrink-0">
                <p className="text-[11px] sm:text-xs md:text-[13px] font-bold text-[rgb(230,0,0)] line-through">
                  Reg{" "}
                  {formatMoney(
                    bundle.originalPrice,
                    String(bundle.currency || pluginConfig.defaultCurrency),
                  )}
                </p>
                <p className="text-sm sm:text-[15px] md:text-[16px] font-bold text-[rgb(102,185,102)]">
                  {bundle.percentageSavings}% OFF
                </p>
                <p className="text-base sm:text-lg md:text-[18px] font-bold text-[rgb(37,42,50)]">
                  {formatMoney(
                    bundle.totalPrice,
                    String(bundle.currency || pluginConfig.defaultCurrency),
                  )}
                </p>
              </div>
            </Label>
          ))}
        </RadioGroup>
      )}

      <div className="element-wrapper">
        <div className="custom" style={{ margin: 0 }}>
          <div className="custom-inner el-760208">
            <style>
              {`
                p.counter {
                  padding: 8px 10px;
                  font-size: 14px;
                  text-align: center;
                }
                
                @media (min-width: 640px) {
                  p.counter {
                    font-size: 16px;
                    padding: 10px;
                  }
                }
                
                @media (min-width: 768px) {
                  p.counter {
                    font-size: 18px;
                  }
                }

                #timer {
                  color: rgb(255, 0, 0);
                  font-weight: bold;
                }
              `}
            </style>
            <p className="counter">
              Order now... only <span id="timer">11</span> left in stock
            </p>
            <script
              dangerouslySetInnerHTML={{
                __html: `
                  function sendMessage(message) {
                    console.log(message);
                  }

                  window.onload = function() {
                    setTimeout(function() {
                      document.getElementById('timer').innerHTML = '14';

                      setTimeout(function() {
                        document.getElementById('timer').innerHTML = '12';

                        setTimeout(function() {
                          document.getElementById('timer').innerHTML = '11';
                          sendMessage("Timer sequence completed.");
                        }, 90000); // 1 minute 30 seconds

                      }, 30000); // 30 seconds

                    }, 5000); // 5 seconds
                  }
                `,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
